/**
 * BetaFeedbackWidget.tsx — Sprint 097
 *
 * Floating feedback button visible to all authenticated users.
 * Appears in the bottom-right corner of the shell.
 * Opens a modal to submit feedback with auto-captured device context.
 *
 * On submit:
 *   1. Calls BetaOperationsCenterService.submitFeedback()
 *   2. For bug reports, also calls BugTrackerService.createFromBetaReport()
 */

import { useState } from 'react';
import {
  Bug,
  Lightbulb,
  Sparkles,
  HelpCircle,
  Heart,
  Star,
  MessageSquarePlus,
  X,
  Send,
  CheckCircle,
} from 'lucide-react';
import {
  captureContext,
  submitFeedback,
  autoScoreSeverity,
  type FeedbackType,
  BETA_OPS_BUILD,
} from '@/services/BetaOperationsCenterService';
import { BugTrackerService } from '@/services/BugTrackerService';

// ─── Types & constants ────────────────────────────────────────────────────────

interface FeedbackTypeOption {
  id:          FeedbackType;
  label:       string;
  icon:        React.FC<{ className?: string; style?: React.CSSProperties }>;
  description: string;
  color:       string;
}

const FEEDBACK_TYPES: FeedbackTypeOption[] = [
  { id: 'bug',         label: 'Bug Report',           icon: Bug,             description: 'Something is broken or not working',       color: '#ef4444' },
  { id: 'feature',     label: 'Feature Request',      icon: Lightbulb,       description: 'A capability you wish existed',             color: '#f59e0b' },
  { id: 'improvement', label: 'Improvement',          icon: Sparkles,        description: 'Make something work better',                color: '#6366f1' },
  { id: 'question',    label: 'Question',             icon: HelpCircle,      description: 'Need help understanding something',         color: '#3b82f6' },
  { id: 'positive',    label: 'Positive Feedback',    icon: Heart,           description: 'Something you love about WorkRoamer',      color: '#10b981' },
  { id: 'rating',      label: 'Overall Experience',   icon: Star,            description: 'Rate your overall beta experience',        color: '#f59e0b' },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function RatingStars({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          aria-label={`Rate ${n} star${n !== 1 ? 's' : ''}`}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 2,
            color: n <= (hover || value) ? '#f59e0b' : 'rgba(255,255,255,0.2)',
            fontSize: 22, lineHeight: 1, transition: 'color 0.1s',
          }}
        >
          ★
        </button>
      ))}
    </div>
  );
}

// ─── Main Widget ─────────────────────────────────────────────────────────────

export default function BetaFeedbackWidget() {
  const [open,     setOpen]     = useState(false);
  const [step,     setStep]     = useState<'type' | 'form' | 'done'>('type');
  const [selType,  setSelType]  = useState<FeedbackType | null>(null);
  const [title,    setTitle]    = useState('');
  const [desc,     setDesc]     = useState('');
  const [rating,   setRating]   = useState(0);
  const [submitting, setSub]    = useState(false);

  const ctx = open ? captureContext() : null;

  function reset() {
    setStep('type'); setSelType(null); setTitle(''); setDesc(''); setRating(0); setSub(false);
  }
  function close() { setOpen(false); setTimeout(reset, 300); }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selType || !ctx) return;
    setSub(true);
    try {
      let bugId: string | undefined;
      // For bug reports: create a Bug Tracker entry first
      if (selType === 'bug') {
        const bug = BugTrackerService.createFromBetaReport({
          reporter:           'Beta Tester',
          device:             ctx.device,
          browser:            ctx.browser,
          platform:           ctx.os,
          buildNumber:        BETA_OPS_BUILD,
          stepsToReproduce:   desc,
          expectedBehavior:   '',
          actualBehavior:     desc,
          severity:           'medium',
          screenshotUrl:      undefined,
        });
        bugId = bug.id;
      }
      const effectiveTitle = title || FEEDBACK_TYPES.find(t => t.id === selType)!.label;
      submitFeedback({
        type:             selType,
        title:            effectiveTitle,
        description:      desc,
        rating:           selType === 'rating' ? rating : undefined,
        severity:         autoScoreSeverity(selType, effectiveTitle, desc),
        operatorProfile:  {
          timezone:  Intl.DateTimeFormat().resolvedOptions().timeZone,
          userTier:  'BETA_TESTER',
          betaCohort: 'cohort-1',
        },
        reportedInBuild:  BETA_OPS_BUILD,
        occurrenceCount:  1,
        popularity:       0,
        ...ctx,
      }, bugId);
      setStep('done');
    } finally {
      setSub(false);
    }
  }

  const selectedDef = selType ? FEEDBACK_TYPES.find(t => t.id === selType)! : null;

  return (
    <>
      {/* ── Floating trigger button ─────────────────────────────────────────── */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Submit beta feedback"
        title="Submit Beta Feedback"
        style={{
          position:     'fixed',
          bottom:       72,
          right:        16,
          zIndex:       9000,
          width:        40,
          height:       40,
          borderRadius: '50%',
          background:   'rgba(99,102,241,0.9)',
          border:       '1px solid rgba(99,102,241,0.5)',
          display:      'flex',
          alignItems:   'center',
          justifyContent: 'center',
          cursor:       'pointer',
          boxShadow:    '0 4px 16px rgba(99,102,241,0.4)',
          transition:   'transform 0.15s, box-shadow 0.15s',
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.1)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
      >
        <MessageSquarePlus style={{ width: 18, height: 18, color: '#fff' }} />
      </button>

      {/* ── Modal backdrop ──────────────────────────────────────────────────── */}
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Beta Feedback"
          style={{
            position: 'fixed', inset: 0, zIndex: 9001,
            background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end',
            padding: '80px 16px 80px 16px',
          }}
          onClick={e => { if (e.target === e.currentTarget) close(); }}
        >
          <div style={{
            background:   'hsl(222,18%,11%)',
            border:       '1px solid rgba(255,255,255,0.1)',
            borderRadius: 12,
            width:        360,
            maxWidth:     '100%',
            maxHeight:    '80vh',
            overflowY:    'auto',
            boxShadow:    '0 20px 60px rgba(0,0,0,0.6)',
          }}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <div>
                <div style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.2em', color: '#6366f1', textTransform: 'uppercase', marginBottom: 2 }}>
                  Beta Operations Center™
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: '#f9fafb' }}>
                  {step === 'done' ? 'Feedback Received' : 'Submit Feedback'}
                </div>
              </div>
              <button onClick={close} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', padding: 4 }}>
                <X style={{ width: 16, height: 16 }} />
              </button>
            </div>

            {/* ── Done state ────────────────────────────────────────────────── */}
            {step === 'done' && (
              <div style={{ padding: 24, textAlign: 'center' }}>
                <CheckCircle style={{ width: 36, height: 36, color: '#10b981', margin: '0 auto 12px' }} />
                <div style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: '#f9fafb', marginBottom: 8 }}>
                  Thank you!
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#9ca3af', lineHeight: 1.7, marginBottom: 20 }}>
                  Your feedback has been logged to the Beta Operations Center™ and will be reviewed by the engineering team.
                </div>
                <button onClick={close} style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, padding: '10px 20px', background: '#10b981', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#fff' }}>
                  Done
                </button>
              </div>
            )}

            {/* ── Type selector ─────────────────────────────────────────────── */}
            {step === 'type' && (
              <div style={{ padding: 16 }}>
                <div style={{ fontFamily: 'monospace', fontSize: 10, color: '#6b7280', letterSpacing: '0.1em', marginBottom: 12 }}>
                  What would you like to share?
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {FEEDBACK_TYPES.map(t => {
                    const Icon = t.icon;
                    return (
                      <button
                        key={t.id}
                        onClick={() => { setSelType(t.id); setStep('form'); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 12,
                          padding: '10px 12px', background: 'rgba(255,255,255,0.03)',
                          border: '1px solid rgba(255,255,255,0.07)',
                          borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                          transition: 'border-color 0.15s, background 0.15s',
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = t.color + '60'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.07)'; }}
                      >
                        <Icon style={{ width: 16, height: 16, color: t.color, flexShrink: 0 }} />
                        <div>
                          <div style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 600, color: '#e5e7eb' }}>{t.label}</div>
                          <div style={{ fontFamily: 'monospace', fontSize: 10, color: '#6b7280', marginTop: 1 }}>{t.description}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Feedback form ─────────────────────────────────────────────── */}
            {step === 'form' && selectedDef && ctx && (
              <form onSubmit={handleSubmit} style={{ padding: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <button type="button" onClick={() => setStep('type')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontFamily: 'monospace', fontSize: 10 }}>
                    ← Back
                  </button>
                  <selectedDef.icon style={{ width: 14, height: 14, color: selectedDef.color }} />
                  <span style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 600, color: '#e5e7eb' }}>{selectedDef.label}</span>
                </div>

                {selType !== 'rating' && (
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontFamily: 'monospace', fontSize: 10, color: '#9ca3af', display: 'block', marginBottom: 4 }}>Title</label>
                    <input
                      value={title}
                      onChange={e => setTitle(e.target.value)}
                      placeholder={`Brief ${selectedDef.label.toLowerCase()} summary…`}
                      maxLength={120}
                      style={{
                        width: '100%', boxSizing: 'border-box',
                        fontFamily: 'monospace', fontSize: 12, padding: '8px 10px',
                        background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 6, color: '#f9fafb', outline: 'none',
                      }}
                    />
                  </div>
                )}

                {selType === 'rating' ? (
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontFamily: 'monospace', fontSize: 10, color: '#9ca3af', display: 'block', marginBottom: 8 }}>Overall Experience</label>
                    <RatingStars value={rating} onChange={setRating} />
                  </div>
                ) : null}

                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontFamily: 'monospace', fontSize: 10, color: '#9ca3af', display: 'block', marginBottom: 4 }}>
                    {selType === 'bug' ? 'Steps to reproduce / what happened' : 'Details'}
                  </label>
                  <textarea
                    value={desc}
                    onChange={e => setDesc(e.target.value)}
                    rows={4}
                    placeholder={selType === 'bug' ? '1. Go to…\n2. Click…\n3. Error appears' : 'Tell us more…'}
                    style={{
                      width: '100%', boxSizing: 'border-box', resize: 'vertical',
                      fontFamily: 'monospace', fontSize: 12, padding: '8px 10px',
                      background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 6, color: '#f9fafb', outline: 'none',
                    }}
                  />
                </div>

                {/* Auto-captured context — shown for transparency */}
                <div style={{ padding: '8px 10px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 6, marginBottom: 14 }}>
                  <div style={{ fontFamily: 'monospace', fontSize: 9, color: '#4b5563', letterSpacing: '0.1em', marginBottom: 6 }}>AUTO-CAPTURED CONTEXT</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 12px' }}>
                    {[
                      ['Build', ctx.buildNumber],
                      ['Device', ctx.device],
                      ['Browser', ctx.browser],
                      ['OS', ctx.os],
                      ['Resolution', ctx.screenResolution],
                      ['Module', ctx.currentModule],
                    ].map(([k, v]) => (
                      <div key={k} style={{ fontFamily: 'monospace', fontSize: 9 }}>
                        <span style={{ color: '#4b5563' }}>{k}: </span>
                        <span style={{ color: '#9ca3af' }}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting || (selType === 'rating' && rating === 0)}
                  style={{
                    width: '100%', padding: '10px', fontFamily: 'monospace', fontSize: 11, fontWeight: 700,
                    background: selType === 'rating' && rating === 0 ? 'rgba(99,102,241,0.3)' : '#6366f1',
                    border: 'none', borderRadius: 6, cursor: submitting ? 'wait' : 'pointer',
                    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    opacity: submitting ? 0.7 : 1,
                  }}
                >
                  <Send style={{ width: 12, height: 12 }} />
                  {submitting ? 'SUBMITTING…' : 'SUBMIT FEEDBACK'}
                </button>
              </form>
            )}

          </div>
        </div>
      )}
    </>
  );
}
